import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getUserAchievements, getActiveUserAchievements, getAchievementsByIds, getUserProgress } from '../../services/achievementService';
import { Achievement, UserAchievementProgress } from '../../types/achievement';
import userService from '../../services/userService';
import AchievementModal from '../achievements/AchievementModal';


interface UserAchievementsProps {
  userId: string;
  gameType?: 'airsoft' | 'paintball' | 'all';
}

// Styled Components
const Container = styled.div`
  width: 100%;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 16px;
  background: ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.1)'};
  color: ${props => props.$active ? '#000000' : '#00d4ff'};
  border: 1px solid #00d4ff;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  
  &:hover {
    background: ${props => props.$active ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 24px;
  font-weight: bold;
  color: #00d4ff;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #b0b0b0;
  text-transform: uppercase;
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const AchievementCard = styled.div<{ $completed: boolean; $rarity: string }>`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => {
    if (props.$completed) {
      switch (props.$rarity.toLowerCase()) {
        case 'legendary': return '#ff6b35';
        case 'epic': return '#9b59b6';
        case 'rare': return '#3498db';
        case 'uncommon': return '#27ae60';
        default: return '#95a5a6';
      }
    }
    return 'rgba(0, 212, 255, 0.2)';
  }};
  border-radius: 12px;
  padding: 20px;
  position: relative;
  transition: all 0.3s ease;
  opacity: ${props => props.$completed ? 1 : 0.7};
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 212, 255, 0.2);
  }
`;

const AchievementIcon = styled.div<{ $completed: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.$completed ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 24px;
`;

const AchievementName = styled.h3`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #ffffff;
  margin: 0 0 10px 0;
  text-align: center;
  font-weight: bold;
`;

const AchievementDescription = styled.p`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #b0b0b0;
  margin: 0 0 15px 0;
  text-align: center;
  line-height: 1.4;
`;

const AchievementRarity = styled.div<{ $rarity: string }>`
  position: absolute;
  top: 10px;
  right: 10px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: bold;
  background: ${props => {
    switch (props.$rarity.toLowerCase()) {
      case 'legendary': return '#ff6b35';
      case 'epic': return '#9b59b6';
      case 'rare': return '#3498db';
      case 'uncommon': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  color: #ffffff;
`;

const AchievementProgress = styled.div`
  margin-top: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #0099cc);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #b0b0b0;
  text-align: center;
`;

const CompletedBadge = styled.div`
  position: absolute;
  top: -5px;
  left: -5px;
  width: 30px;
  height: 30px;
  background: #27ae60;
  border: 2px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  font-family: 'Courier New', monospace;
  color: #00d4ff;
  text-align: center;
  margin: 40px 0;
  font-size: 16px;
`;

const NoAchievementsMessage = styled.div`
  font-family: 'Courier New', monospace;
  color: #b0b0b0;
  text-align: center;
  padding: 40px 20px;
  font-size: 14px;
  font-style: italic;
`;

const UserAchievements: React.FC<UserAchievementsProps> = ({ userId, gameType = 'all' }) => {
  const [completedAchievements, setCompletedAchievements] = useState<Achievement[]>([]);
  const [progressAchievements, setProgressAchievements] = useState<UserAchievementProgress[]>([]);
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [completedAchievementIds, setCompletedAchievementIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'completed' | 'progress' | 'all'>('completed');
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadAllData();
  }, [userId, gameType]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user profile first to get achievementsIds
      const userProfile = await userService.getPlayerProfile(userId);
      const achievementIds = userProfile.achievementsIds || [];
      setCompletedAchievementIds(achievementIds);

      // Load completed achievements, progress data, and all achievements in parallel
      const [completedData, progressData, allData] = await Promise.all([
        achievementIds.length > 0 ? getAchievementsByIds(achievementIds) : Promise.resolve([]),
        getUserProgress(userId),
        getActiveUserAchievements()
      ]);

      setCompletedAchievements(completedData);
      setProgressAchievements(progressData);
      setAllAchievements(allData);
    } catch (err) {
      setError('Failed to load achievements data');
    } finally {
      setLoading(false);
    }
  };


  const getAchievementProgress = (achievementId: string): UserAchievementProgress | null => {
    return progressAchievements.find(p => p.achievementId === achievementId) || null;
  };

  const isAchievementCompleted = (achievementId: string): boolean => {
    return completedAchievementIds.includes(achievementId);
  };

  const calculateProgress = (achievement: Achievement, progress: UserAchievementProgress | null): number => {
    // If achievement is completed (in user's achievementsIds), always return 100%
    if (isAchievementCompleted(achievement.id)) return 100;
    
    // If no progress record exists, return 0%
    if (!progress) return 0;
    
    // If progress record shows completed, return 100%
    if (progress.isCompleted) return 100;

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'win_games':
      case 'play_games':
      case 'win_mission':
      case 'play_mission':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'win_streak':
      case 'play_streak':
        return Math.min(100, (progressInfo.currentStreak / conditions.count) * 100);
      case 'daily_games':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'weekly_games':
        return Math.min(100, (progressInfo.weeklyCount / conditions.count) * 100);
      case 'complete_all_missions':
        return Math.min(100, (progressInfo.completedMissions.length / conditions.count) * 100);
      default:
        return 0;
    }
  };

  const getProgressText = (achievement: Achievement, progress: UserAchievementProgress | null): string => {
    // If achievement is completed (in user's achievementsIds), always show "Completed!"
    if (isAchievementCompleted(achievement.id)) return 'Completed!';
    
    // If no progress record exists, show 0 progress
    if (!progress) return `0 / ${achievement.conditions.count}`;
    
    // If progress record shows completed, show "Completed!"
    if (progress.isCompleted) return 'Completed!';

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'win_games':
      case 'play_games':
      case 'win_mission':
      case 'play_mission':
        return `${progressInfo.dailyCount} / ${conditions.count}`;
      case 'win_streak':
      case 'play_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} streak`;
      case 'daily_games':
        return `${progressInfo.dailyCount} / ${conditions.count} today`;
      case 'weekly_games':
        return `${progressInfo.weeklyCount} / ${conditions.count} this week`;
      case 'complete_all_missions':
        return `${progressInfo.completedMissions.length} / ${conditions.count} missions`;
      default:
        return `0 / ${conditions.count}`;
    }
  };


  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setShowModal(true);
  };

  const stats = {
    completed: completedAchievements.filter(a => gameType === 'all' || a.gameType === 'any' || a.gameType === gameType).length,
    inProgress: progressAchievements.filter(p => {
      // For progress items, we can't easily filter by gameType without loading achievement data
      // For now, just count all progress items
      return !p.isCompleted;
    }).length,
    total: allAchievements.length > 0 
      ? allAchievements.filter(a => gameType === 'all' || a.gameType === 'any' || a.gameType === gameType).length
      : completedAchievements.length + progressAchievements.length
  };

  if (loading) {
    return <LoadingMessage>Loading achievements...</LoadingMessage>;
  }

  if (error) {
    return <NoAchievementsMessage>{error}</NoAchievementsMessage>;
  }

  return (
    <Container>
      {/* Stats */}
      <StatsContainer>
        <StatCard>
          <StatValue>{stats.completed}</StatValue>
          <StatLabel>Completed</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.inProgress}</StatValue>
          <StatLabel>In Progress</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total Available</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</StatValue>
          <StatLabel>Completion Rate</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Filters */}
      <FilterContainer>
        <FilterGroup>
          <FilterButton 
            $active={viewMode === 'completed'} 
            onClick={() => setViewMode('completed')}
          >
            Completed ({stats.completed})
          </FilterButton>
          <FilterButton 
            $active={viewMode === 'progress'} 
            onClick={() => setViewMode('progress')}
          >
            In Progress ({stats.inProgress})
          </FilterButton>
          <FilterButton 
            $active={viewMode === 'all'} 
            onClick={() => setViewMode('all')}
          >
            All Available
          </FilterButton>
        </FilterGroup>

      </FilterContainer>

      {/* Achievements Grid */}
      <AchievementsGrid>
        {viewMode === 'completed' && completedAchievements.map(achievement => (
          <AchievementCard
            key={achievement.id}
            $completed={true}
            $rarity={achievement.rarity}
            onClick={() => handleAchievementClick(achievement)}
          >
            <CompletedBadge>✓</CompletedBadge>
            
            <AchievementRarity $rarity={achievement.rarity}>
              {achievement.rarity}
            </AchievementRarity>

            <AchievementIcon $completed={true}>
              {achievement.iconUrl ? (
                <img src={achievement.iconUrl} alt={achievement.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
              ) : (
                '🏆'
              )}
            </AchievementIcon>

            <AchievementName>{achievement.name}</AchievementName>
            <AchievementDescription>{achievement.description}</AchievementDescription>

            <ProgressText>Completed!</ProgressText>
          </AchievementCard>
        ))}

        {viewMode === 'progress' && progressAchievements.map(progress => {
          // Find the corresponding achievement for this progress
          const achievement = allAchievements.find(a => a.id === progress.achievementId);
          if (!achievement) return null;
          
          const progressPercent = calculateProgress(achievement, progress);
          const progressText = getProgressText(achievement, progress);

          return (
            <AchievementCard
              key={progress.id}
              $completed={false}
              $rarity={achievement.rarity}
              onClick={() => handleAchievementClick(achievement)}
            >
              <AchievementRarity $rarity={achievement.rarity}>
                {achievement.rarity}
              </AchievementRarity>

              <AchievementIcon $completed={false}>
                {achievement.iconUrl ? (
                  <img src={achievement.iconUrl} alt={achievement.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : (
                  '🎯'
                )}
              </AchievementIcon>

              <AchievementName>{achievement.name}</AchievementName>
              <AchievementDescription>{achievement.description}</AchievementDescription>

              <AchievementProgress>
                <ProgressBar>
                  <ProgressFill $progress={progressPercent} />
                </ProgressBar>
                <ProgressText>{progressText}</ProgressText>
              </AchievementProgress>
            </AchievementCard>
          );
        })}

        {viewMode === 'all' && allAchievements.map(achievement => {
          const isCompleted = isAchievementCompleted(achievement.id);
          const progress = getAchievementProgress(achievement.id);
          const progressPercent = calculateProgress(achievement, progress);
          const progressText = getProgressText(achievement, progress);

          return (
            <AchievementCard
              key={achievement.id}
              $completed={isCompleted}
              $rarity={achievement.rarity}
              onClick={() => handleAchievementClick(achievement)}
            >
              {isCompleted && (
                <CompletedBadge>✓</CompletedBadge>
              )}
              
              <AchievementRarity $rarity={achievement.rarity}>
                {achievement.rarity}
              </AchievementRarity>

              <AchievementIcon $completed={isCompleted}>
                {achievement.iconUrl ? (
                  <img src={achievement.iconUrl} alt={achievement.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : (
                  isCompleted ? '🏆' : '🎯'
                )}
              </AchievementIcon>

              <AchievementName>{achievement.name}</AchievementName>
              <AchievementDescription>{achievement.description}</AchievementDescription>

              {!isCompleted && progress && (
                <AchievementProgress>
                  <ProgressBar>
                    <ProgressFill $progress={progressPercent} />
                  </ProgressBar>
                  <ProgressText>{progressText}</ProgressText>
                </AchievementProgress>
              )}

              {isCompleted && (
                <ProgressText>Completed!</ProgressText>
              )}
            </AchievementCard>
          );
        })}

        {((viewMode === 'completed' && completedAchievements.length === 0) ||
          (viewMode === 'progress' && progressAchievements.length === 0) ||
          (viewMode === 'all' && allAchievements.length === 0)) && (
          <NoAchievementsMessage>
            {viewMode === 'completed' && 'No completed achievements yet.'}
            {viewMode === 'progress' && 'No achievements in progress.'}
            {viewMode === 'all' && 'No achievements available.'}
          </NoAchievementsMessage>
        )}
      </AchievementsGrid>

      {/* Achievement Modal */}
      {selectedAchievement && (
        <AchievementModal
          achievement={selectedAchievement}
          progress={getAchievementProgress(selectedAchievement.id)}
          isCompleted={isAchievementCompleted(selectedAchievement.id)}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedAchievement(null);
          }}
        />
      )}
    </Container>
  );
};

export default UserAchievements;